import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Trophy,
  Save,
  AlertTriangle,
  ArrowLeft,
  Medal,
  CheckCircle,
  Users,
} from "lucide-react";
import { generateClient } from "aws-amplify/api";
import {
  listEvents,
  listEventPlayerResults,
  listPlayerConstructs,
  getLeague,
} from "../../graphql/queries";
import { createEventTopCutResult } from "../../graphql/mutations";
import type {
  Event,
  EventPlayerResult,
  PlayerConstruct,
} from "../../lib/types";
import { calculateTop8Points, getLeagueScoring } from "../../lib/scoring";

const client = generateClient();

const EventPlacements: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const queryClient = useQueryClient();
  const [placements, setPlacements] = useState<Record<string, number>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch event data
  const {
    data: event,
    isLoading: eventLoading,
    error: eventError,
  } = useQuery({
    queryKey: ["event", eventId],
    queryFn: async () => {
      const result = await client.graphql({
        query: listEvents,
        variables: {
          filter: { id: { eq: eventId } },
          limit: 1,
        },
      });
      return result.data.listEvents.items[0] as unknown as Event;
    },
    enabled: !!eventId,
  });

  const { data: league, isLoading: leagueLoading } = useQuery({
    queryKey: ["league", event?.leagueId],
    queryFn: async () => {
      const result = await client.graphql({
        query: getLeague,
        variables: { id: event!.leagueId },
      });
      return result.data.getLeague;
    },
    enabled: !!event?.leagueId,
  });
  const scoring = getLeagueScoring(league);

  // Fetch event player results
  const {
    data: eventPlayerResults,
    isLoading: resultsLoading,
    error: resultsError,
  } = useQuery({
    queryKey: ["eventPlayerResults", eventId],
    queryFn: async () => {
      let allItems: any[] = [];
      let nextToken: string | null | undefined = undefined;

      // Fetch all pages of results
      do {
        const result: any = await client.graphql({
          query: listEventPlayerResults,
          variables: {
            filter: { eventId: { eq: eventId } },
            limit: 100,
            nextToken: nextToken,
          },
        });

        allItems = [...allItems, ...result.data.listEventPlayerResults.items];
        nextToken = result.data.listEventPlayerResults.nextToken;
      } while (nextToken);

      return allItems as unknown as EventPlayerResult[];
    },
    enabled: !!eventId,
  });

  // Fetch player constructs
  const {
    data: playerConstructs,
    isLoading: constructsLoading,
    error: constructsError,
  } = useQuery({
    queryKey: ["playerConstructs"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listPlayerConstructs,
        variables: { limit: 1000 },
      });
      return result.data.listPlayerConstructs
        .items as unknown as PlayerConstruct[];
    },
  });

  // Note: We could fetch existing top cut results here if needed for editing existing placements

  // Define the player type for the UI
  type PlayerForUI = {
    id: string;
    playerConstructId: string;
    name: string;
    record: string;
    points: number;
    rank: number;
    omw: number;
    gw: number;
    ogw: number;
  };

  // Combine player results with player constructs to get full player info
  const players = React.useMemo((): PlayerForUI[] => {
    if (!eventPlayerResults || !playerConstructs) return [];

    return eventPlayerResults
      .map((result): PlayerForUI | null => {
        const construct = playerConstructs.find(
          (c) => c.id === result.playerConstructId
        );
        if (!construct) return null;

        return {
          id: result.id,
          playerConstructId: result.playerConstructId,
          name: construct.name,
          record: `${result.wins}-${result.losses}-${result.draws}`,
          points: result.points,
          rank: result.rank,
          omw: result.omw,
          gw: result.gw,
          ogw: result.ogw,
        };
      })
      .filter((player): player is PlayerForUI => player !== null)
      .sort((a, b) => a.rank - b.rank); // Sort by rank
  }, [eventPlayerResults, playerConstructs]);

  const handlePlacementChange = (playerId: string, placement: string) => {
    const placementNumber = parseInt(placement);
    if (placementNumber >= 1 && placementNumber <= 8) {
      setPlacements((prev) => ({
        ...prev,
        [playerId]: placementNumber,
      }));
    }
  };

  const getPlacementPoints = (placement: number) => {
    return placement >= 1 && placement <= 8
      ? calculateTop8Points(placement as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8, scoring)
      : 0;
  };

  const validatePlacements = () => {
    const usedPlacements = Object.values(placements);
    const uniquePlacements = new Set(usedPlacements);

    // Check if all 8 placements are set
    if (usedPlacements.length !== 8) {
      return { valid: false, error: "All 8 placements must be set" };
    }

    // Check if all placements are unique
    if (uniquePlacements.size !== 8) {
      return { valid: false, error: "Each placement must be unique" };
    }

    // Check if placements are 1-8
    const validPlacements = usedPlacements.every((p) => p >= 1 && p <= 8);
    if (!validPlacements) {
      return { valid: false, error: "Placements must be between 1 and 8" };
    }

    return { valid: true, error: null };
  };

  const handleSavePlacements = async () => {
    const validation = validatePlacements();
    if (!validation.valid) {
      alert(`Error: ${validation.error}`);
      return;
    }

    if (!eventId) {
      alert("Event ID is missing");
      return;
    }

    setIsSubmitting(true);
    try {
      // Create EventTopCutResult records for each placement
      const placementPromises = Object.entries(placements).map(
        async ([playerResultId, rank]) => {
          const playerResult = eventPlayerResults?.find(
            (r) => r.id === playerResultId
          );
          if (!playerResult) {
            throw new Error(
              `Player result not found for ID: ${playerResultId}`
            );
          }

          const bonusPoints = getPlacementPoints(rank);

          return client.graphql({
            query: createEventTopCutResult,
            variables: {
              input: {
                eventId,
                playerConstructId: playerResult.playerConstructId,
                rank,
                bonusPoints,
              },
            },
          });
        }
      );

      await Promise.all(placementPromises);
      await queryClient.invalidateQueries();
      alert("Top 8 placements saved successfully!");
      setPlacements({});
    } catch (error) {
      console.error("Error saving placements:", error);
      alert("Error saving placements. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return "🥇";
      case 2:
        return "🥈";
      case 3:
        return "🥉";
      default:
        return null;
    }
  };

  // Show loading state
  if (eventLoading || leagueLoading || resultsLoading || constructsLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="ml-2">Loading event data...</span>
        </div>
      </div>
    );
  }

  // Show error state
  if (eventError || resultsError || constructsError) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <p className="text-red-600">
            Error loading event data. Please try again.
          </p>
        </div>
      </div>
    );
  }

  // Show no data state
  if (!event || !players.length) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <p className="text-muted-foreground">No event data found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/admin">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Admin
              </Link>
            </Button>
          </div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Trophy className="h-8 w-8 text-primary" />
            Set Top 8 Placements
          </h1>
          <p className="text-muted-foreground">
            {event.name} - {event.format} • {event.date}
          </p>
        </div>
        <div className="flex gap-2 mt-4 sm:mt-0">
          <Button
            onClick={handleSavePlacements}
            disabled={isSubmitting || !validatePlacements().valid}
            className="min-w-[120px]"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Placements
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Event Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            Event Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Format
              </p>
              <p className="text-lg font-semibold">{event.format}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Participants
              </p>
              <p className="text-lg font-semibold">{players.length} players</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Status
              </p>
              <p className="text-lg font-semibold flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                {event.isCompleted ? "Completed" : "In Progress"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Top 8 Placement Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Medal className="h-5 w-5" />
            Top 8 Placements
          </CardTitle>
          <CardDescription>
            Set the final rankings for the top 8 players. Each placement must be
            unique.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {players.slice(0, 8).map((player) => {
              if (!player) return null;
              return (
                <div
                  key={player.id}
                  className="flex items-center justify-between p-4 border rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{player.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {player.record} • {player.points} points • Rank{" "}
                          {player.rank}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm font-medium">Placement</p>
                      <p className="text-sm text-muted-foreground">
                        {placements[player.id] ? (
                          <span className="flex items-center gap-1">
                            {placements[player.id]}
                            {getRankIcon(placements[player.id])}
                            <span className="text-xs">
                              (+{getPlacementPoints(placements[player.id])} pts)
                            </span>
                          </span>
                        ) : (
                          "Not set"
                        )}
                      </p>
                    </div>

                    <Select
                      value={placements[player.id]?.toString() || ""}
                      onValueChange={(value) =>
                        handlePlacementChange(player.id, value)
                      }
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue placeholder="-" />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 8 }, (_, i) => i + 1).map(
                          (placement) => (
                            <SelectItem
                              key={placement}
                              value={placement.toString()}
                            >
                              {placement}
                            </SelectItem>
                          )
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Placement Summary */}
      {Object.keys(placements).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Placement Summary</CardTitle>
            <CardDescription>
              Review the current placements before saving
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <h4 className="font-semibold mb-3">Current Placements</h4>
                <div className="space-y-2">
                  {Array.from({ length: 8 }, (_, i) => i + 1).map(
                    (placement) => {
                      const playerId = Object.keys(placements).find(
                        (key) => placements[key] === placement
                      );
                      const player = playerId
                        ? players.find((p) => p.id === playerId)
                        : undefined;

                      return (
                        <div
                          key={placement}
                          className="flex items-center justify-between p-2 border rounded"
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{placement}</span>
                            {getRankIcon(placement)}
                          </div>
                          <div className="text-right">
                            <p className="font-medium">
                              {player?.name || "Not assigned"}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              +{getPlacementPoints(placement)} points
                            </p>
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-3">Point Breakdown</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">1st Place:</span>
                    <span>+10 points</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">2nd Place:</span>
                    <span>+6 points</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      3rd-4th Place:
                    </span>
                    <span>+3 points</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      5th-8th Place:
                    </span>
                    <span>+1 point</span>
                  </div>
                </div>

                <div className="mt-4 p-3 bg-muted rounded-lg">
                  <p className="text-sm font-medium">Total Bonus Points</p>
                  <p className="text-2xl font-bold">
                    {Object.values(placements).reduce(
                      (sum, placement) => sum + getPlacementPoints(placement),
                      0
                    )}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Validation Warnings */}
      {Object.keys(placements).length > 0 && (
        <Card className="border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-800 dark:text-yellow-200">
              <AlertTriangle className="h-5 w-5" />
              Important Notice
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                Please review all placements carefully before saving. Once
                saved, these placements will be applied to the league standings
                and cannot be easily modified.
              </p>
              <div className="text-sm text-yellow-700 dark:text-yellow-300">
                <p>• All 8 placements must be set</p>
                <p>• Each placement must be unique (1-8)</p>
                <p>• Bonus points will be added to league standings</p>
                <p>• This action will finalize the event</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EventPlacements;
